/* eslint-disable no-param-reassign */
import $ from 'jquery';
import _ from 'underscore';
import { ceil } from 'lodash';
import React from 'react';
import ReactDOM from 'react-dom';
import { CampaignuiBaseColumn } from '@bingads-webui-campaign/campaignui-base-column';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { AdGroup } from '@bingads-webui-campaign/campaign-ui-schemas';
import { BiddingStrategyToRangeKey, DefaultBidCPVWarningThreshold, BiddingStrategyToPricingModel } from '@bingads-webui-campaign/audience-campaign-shared';
import { defaultMSStoreMinSuggestionBid } from '@bingads-webui-campaign-legacy/budget/shared/defaultBid';
import { isMSStoreAdsCampaign } from '@bingads-webui-campaign-react/ms-store-ads-common';
import { Alert } from '@bingads-webui-react/alert';
import { isXandrServing, isXandrVideoAdsCampaign } from '@bingads-webui-campaign-react/constants';
import { InlineEditableColumnPrompt } from './inline-editable/prompt';
import template from './bid-base-column.pug';

const appConfig = globalContext.require('component/config/index');
const permissions = appConfig.get('Permissions');

export const commonBidStrategyListToDisableBidAmountEdit = (keepEnabled = []) => ['ManualCpc', 'ManualCpv', 'ManualCpm', 'ManualCpa', 'EnhancedCpc', 'MaxClicks', 'MaxConversions', 'TargetCpa',
  'TargetRoas', 'MaxConversionValue', 'TargetImpressionShare', 'CostPerSale', 'PercentCpc', 'Commission', 'MaxReach', 'MaxImpressions'].filter(bidStrategyType => !keepEnabled.includes(bidStrategyType));

export class BidBaseColumn extends CampaignuiBaseColumn {
  constructor({
    name, property, title, i18n, view, bidStrategyListToDisableBidAmountEdit, sortable,
  }) {
    super({
      name,
      property: property || [name, 'IsBidLandscapeAvailable', 'DisplayBiddingStrategyType'],
      title: title || 'Bid',
      sortable: sortable || name,
      type: 'number',
      helpId: `Page_CampaignGrids_${name}`,
      enableEdit: item =>
        !_.contains(bidStrategyListToDisableBidAmountEdit, item.DisplayBiddingStrategyType),
      helpAriaLabel: i18n.getString('Click to learn more about {{item}}', { item: title }),
    });

    this.view = view;
    this.i18n = i18n;
    this.numberFormatter = i18n.decimalToFixedFormatter({ digits: appConfig.get('globalization.number.numberOfDigits') });
    this.bidStrategyListToDisableBidAmountEdit = bidStrategyListToDisableBidAmountEdit;
    this.defaultMSStoreMinSuggestionBid = defaultMSStoreMinSuggestionBid();
  }

  onPrompt({
    item, event, newPosition,
  }) {
    if ($(event.target).hasClass('bidlandscape_icon')) {
      event.preventDefault();
      return;
    }

    const campaignType = (item && item.Campaign) ? _.result(item.Campaign, 'CampaignType') : '';
    this.campaignSubType = (item && item.Campaign) ? _.result(item.Campaign, 'CampaignSubType') : '';
    this.campaignSettings = (item && item.Campaign) ? _.result(item.Campaign, 'CampaignSettings') : [];
    const useProductGroupBidRange = permissions.IsBidLessThan5CentsInShoppingCampaignEnabled && (campaignType === 'Shopping');
    const isHotelCampaign = campaignType === 'Hotel';
    const isMSStoreAdsCampaignEnabled = isMSStoreAdsCampaign(item.Campaign);
    let bidRangeKey;
    if (useProductGroupBidRange) {
      bidRangeKey = 'ProductGroupBidRange';
      if (isMSStoreAdsCampaignEnabled) {
        bidRangeKey = 'WindowsStoreAdsBidRange';
      }
    } else if (isHotelCampaign) {
      bidRangeKey = 'KeywordBidRange';
      if (this.name === 'PercentCpcBid' || this.name === 'CommissionRate') {
        bidRangeKey = `Keyword${this.name}Range`;
      }
    } else if (isXandrVideoAdsCampaign(this.campaignSubType) && isXandrServing(this.campaignSubType, this.campaignSettings)) {
      if (item.DisplayBiddingStrategyType === 'ManualCpv') {
        bidRangeKey = 'VideoViewsCPVBidRange';
      } else {
        bidRangeKey = 'VideoViewsCPMBidRange';
      }
    } else {
      bidRangeKey = BiddingStrategyToRangeKey[item.DisplayBiddingStrategyType] || 'KeywordBidRange';
    }


    let min = _.result(appConfig.get(bidRangeKey), 'FloorValue');
    const max = _.result(appConfig.get(bidRangeKey), 'CeilingValue');

    if (isXandrServing(this.campaignSubType, this.campaignSettings)
    && permissions.IsDealEnabledForMsAds
    && item.DealMinimumBid && item.DealMinimumBid < max) {
      min = _.max([min, ceil(item.DealMinimumBid, 2)]);
    }

    const currentName = (this.name === 'PercentCpcBid' || this.name === 'CommissionRate') ? `AdGroup${this.name}` : this.name;
    const schemaOrigin = AdGroup;
    const schemaCopy = {
      ...schemaOrigin,
      properties: {
        ...schemaOrigin.properties,
        [currentName]: {
          ...schemaOrigin.properties[currentName],
          maximum: null,
          minimum: null,
        },
      },
    };
    let errorMsg = '';
    const groupClass = `grid-group-${_.result(this.config, 'group', 'other')}`;
    this.isDefaultBidCPVAppended = false;
    const pricingModel = BiddingStrategyToPricingModel[item.DisplayBiddingStrategyType];
    const isCommissionOrPercentCpc = item && (item.DisplayBiddingStrategyType === 'Commission' || item.DisplayBiddingStrategyType === 'PercentCpc');

    InlineEditableColumnPrompt({
      customClass: `bid-editor-prompt ${groupClass}`,
      // the empty space is to ensure the customHint element is rendered with empty content. So that we can put the warning in the correct position
      customHint: isMSStoreAdsCampaignEnabled ? ' ' : '',
      position: newPosition,
      item,
      view: this.view,
      i18n: this.i18n,
      fieldName: currentName,
      customValidators: {
        [currentName]: {
          isValid: (bid) => {
            this.showDefaultBidWarning = false;
            // the setTimeout is ensure the customHint element is rendered before we try to append the warning
            setTimeout(() => this.showLowerThanMinSuggestionBidWarning(bid, isMSStoreAdsCampaignEnabled));
            if (_.isNull(bid)) {
              errorMsg = this.i18n.getString('AdGroupSummary_SearchBidRequired');
              this.updateDefaultBidWarning();
              return false;
            } else if (isCommissionOrPercentCpc && (bid <= min || bid > max)) {
              errorMsg = this.i18n.getString('Validation_Number_Range_ExcludeMin', { min, max });
              this.updateDefaultBidWarning();
              return false;
            } else if (bid < min || bid > max) {
              errorMsg = this.i18n.getString('Validation_Number_Range_Between', { min, max });
              this.updateDefaultBidWarning();
              return false;
            } else if (pricingModel === 'CPV' && (
              (!isXandrVideoAdsCampaign(this.campaignSubType) && bid > DefaultBidCPVWarningThreshold)
            )) {
              this.showDefaultBidWarning = true;
            }
            this.updateDefaultBidWarning();
            return true;
          },
          message: () => errorMsg,
        },
      },
      adGroupSchema: schemaCopy,
    });
  }

  initDefaultBidWarning(element, warning) {
    ReactDOM.render(
      <Alert
        message={warning}
        level="Warning"
      />,
      element
    );
  }

  updateDefaultBidWarning() {
    const defaultBidCPVWarningParent = $('.bid-editor-prompt .form-inline:last-of-type');
    const defaultBidCPVWarningId = 'default-bid-cpv-warning';
    if (!this.isDefaultBidCPVAppended && defaultBidCPVWarningParent.length === 1) {
      defaultBidCPVWarningParent.append(`<div id="${defaultBidCPVWarningId}"><div>`);
      this.defaultBidCPVWarning = document.getElementById(defaultBidCPVWarningId);
      $(`#${defaultBidCPVWarningId}`).hide();
      const warning = this.i18n.getString('Audience_Campaign_DefaultBid_CPV_Warning');
      this.initDefaultBidWarning(this.defaultBidCPVWarning, warning);
      this.isDefaultBidCPVAppended = true;
    }
    if (this.defaultBidCPVWarning) {
      if (this.showDefaultBidWarning) {
        $(`#${defaultBidCPVWarningId}`).show();
      } else {
        $(`#${defaultBidCPVWarningId}`).hide();
      }
    }
  }

  formatBidString(name, bid, bidstrategy) {
    const isAutoBid = bidstrategy === 'TargetCpa' || bidstrategy === 'TargetRoas' || bidstrategy === 'MaxConversionValue' || bidstrategy === 'MaxConversions' || bidstrategy === 'MaxClicks' || bidstrategy === 'TargetImpressionShare';
    if (permissions.dynamic && permissions.dynamic.HideAutoBidValueInGrid && isAutoBid && bid !== null) {
      return this.i18n.getString('BidStrategy_BidAuto_HideValue');
    }
    if (bid) {
      const formattedBid = this.numberFormatter(bid);
      if (name === 'PercentCpcBid' || name === 'CommissionRate') {
        return `${formattedBid}%`;
      }
      switch (bidstrategy) {
        case 'EnhancedCpc':
          return `${formattedBid} ${this.i18n.getString('BidStrategy_BidEnhanced')}`;
        case 'TargetCpa':
        case 'TargetRoas':
        case 'MaxConversionValue':
        case 'MaxConversions':
        case 'MaxClicks':
        case 'TargetImpressionShare':
          return this.i18n.getString('BidStrategy_BidAuto').replace('{0}', formattedBid);
        case 'CostPerSale':
          return this.i18n.getString('BidStrategy_BidAuto_CPS');
        case 'ManualCpc':
        default:
          return formattedBid;
      }
    }

    return '';
  }

  pickProperty(item) {
    const {
      IsBidLandscapeAvailable,
      DisplayBiddingStrategyType: bidstrategy,
      Id: id,
      Campaign: campaign,
    } = item;
    let bid = null;
    const {
      IsAdgroupLandscapeV2Enabled,
    } = permissions;

    if (campaign && campaign.CampaignType !== 'LinkedIn') {
      if (this.name === 'PercentCpcBid' || this.name === 'CommissionRate') {
        bid = item[this.name] && item[this.name].RateAmount && item[this.name].RateAmount.Amount;
        item[`AdGroup${this.name}`] = bid;
      } else {
        bid = item[this.name];
      }
    }

    let hasBidlandscape = IsBidLandscapeAvailable;
    if (campaign && campaign.CampaignType === 'Audience' && bidstrategy !== 'ManualCpc') {
      hasBidlandscape = hasBidlandscape && IsAdgroupLandscapeV2Enabled;
    }

    return {
      hasBidlandscape,
      bid: this.formatBidString(this.name, bid, bidstrategy),
      id,
      isBidReadOnly: permissions.IsReadOnlyUser ||
        appConfig.get('CurrentAccountInActive') ||
        _.contains(this.bidStrategyListToDisableBidAmountEdit, bidstrategy),
      isSearchMediumType: item.Medium !== 'Content',
      i18n: this.i18n,
    };
  }

  showLowerThanMinSuggestionBidWarning(bid, isMSStoreAdsCampaignEnabled) {
    if (!isMSStoreAdsCampaignEnabled) return;
    const customHint = $('.bid-editor-prompt .customHint');
    if (bid < this.defaultMSStoreMinSuggestionBid) {
      customHint.html(`<div class="alert alert-info" style="width: 180px; margin-top: 6px">
          <div class="alert-image" />
          <div class="alert-info-inline">
            ${this.i18n.getString(_TL_('MinSuggestionBidWarning'), { minSuggestionBid: this.defaultMSStoreMinSuggestionBid })}
          </div>
        </div>`);
      customHint.show();
    } else {
      customHint.hide();
    }
  }
  get template() {
    return template;
  }
}
